/* eslint-disable react-hooks/set-state-in-effect */
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  createTableColumn,
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  Dropdown,
  Option,
  makeStyles,
  SearchBox,
  TableCellLayout,
  tokens,
  type DropdownProps,
  type TableColumnDefinition,
  Drawer,
  DrawerHeaderTitle,
  DrawerBody,
  Text,
} from "@fluentui/react-components";
import { useMsal } from "@azure/msal-react";
import { ROLES } from "../constants/roles";
import "../index.css";
import { DataService } from "../services/dataService";
import type { IProjektItem } from "../models/IProjektItem";
import { Add24Regular, Dismiss24Regular } from "@fluentui/react-icons";
import Projektanlage from "./components/projektanlage";
import { formatDate } from "../utils/format";
import { getCurrentRole } from "../utils/role";

const useStyles = makeStyles({
  container: {
    padding: `0 ${tokens.spacingHorizontalL}`,
    height: "100%",
    overflow: "auto",
  },
  dataGridHeader: {
    backgroundColor: tokens.colorNeutralBackground3,
  },
});

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { instance, accounts } = useMsal();
  const accountInfo = accounts[0] || null;
  const styles = useStyles();
  const isDashboardRoot = location.pathname === "/";
  const dataService = React.useMemo(() => {
    return accountInfo ? new DataService(instance, accountInfo) : null;
  }, [instance, accountInfo]);

  const [roles, setRoles] = useState<string[]>(getCurrentRole());
  const STORAGE_FILTER_KEY = "curriculumDashboardFilters";
  const DEFAULT_FILTERS = {
    filterText: "",
    typFilter: "Alle Typen",
    studienartFilter: "Alle Studienarten",
    statusFilter: "Alle Status",
  } as const;

  const getInitialFilters = () => {
    try {
      const savedFilters = localStorage.getItem(STORAGE_FILTER_KEY);
      if (!savedFilters) {
        return DEFAULT_FILTERS;
      }

      const parsedFilters = JSON.parse(savedFilters);

      return {
        filterText:
          typeof parsedFilters.filterText === "string"
            ? parsedFilters.filterText
            : DEFAULT_FILTERS.filterText,
        typFilter:
          parsedFilters.typFilter === "Alle Typen" ||
          parsedFilters.typFilter === "Neuentwicklung" ||
          parsedFilters.typFilter === "Überarbeitung"
            ? parsedFilters.typFilter
            : DEFAULT_FILTERS.typFilter,
        studienartFilter:
          parsedFilters.studienartFilter === "Alle Studienarten" ||
          parsedFilters.studienartFilter === "Studiengang" ||
          parsedFilters.studienartFilter === "Lehrgang"
            ? parsedFilters.studienartFilter
            : DEFAULT_FILTERS.studienartFilter,
        statusFilter:
          parsedFilters.statusFilter === "Alle Status" ||
          parsedFilters.statusFilter === "In Planung" ||
          parsedFilters.statusFilter === "Freigegeben" ||
          parsedFilters.statusFilter === "Akkreditiert " ||
          parsedFilters.statusFilter === "Wird nicht weiterverfolgt" ||
          parsedFilters.statusFilter === "Akkreditiert mit Auflagen"
            ? parsedFilters.statusFilter
            : DEFAULT_FILTERS.statusFilter,
      };
    } catch (error) {
      console.error("Error loading filters from localStorage:", error);
      return DEFAULT_FILTERS;
    }
  };

  const [filterText, setFilterText] = useState(
    () => getInitialFilters().filterText,
  );
  const [typFilter, setTypFilter] = useState(
    () => getInitialFilters().typFilter,
  );
  const [studienartFilter, setStudienartFilter] = useState(
    () => getInitialFilters().studienartFilter,
  );
  const [statusFilter, setStatusFilter] = useState(
    () => getInitialFilters().statusFilter,
  );
  const [items, setItems] = useState([] as IProjektItem[]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerSize, setDrawerSize] = useState<"medium" | "large">("medium");

  useEffect(() => {
    const handleRoleChange = () => {
      console.log("Dashboard detected role change event");
      const newRoles = getCurrentRole();
      console.log("New roles after event:", newRoles);
      setRoles(newRoles);
    };

    console.log("Dashboard mounted, initial roles:", roles);

    // Check if role was set between mounting and useEffect running
    const currentRoles = getCurrentRole();
    if (currentRoles.length > 0 && roles.length === 0) {
      console.log(
        "Role was set before event listener - updating now:",
        currentRoles,
      );
      setRoles(currentRoles);
    }

    window.addEventListener("role-changed", handleRoleChange);
    window.addEventListener("storage", handleRoleChange);

    return () => {
      window.removeEventListener("role-changed", handleRoleChange);
      window.removeEventListener("storage", handleRoleChange);
    };
  }, [roles]);

  // Case-insensitive role checking
  const hasRole = (roleName: string) =>
    roles.some((r) => r.toLowerCase() === roleName.toLowerCase());

  const hasPermission = ROLES.some((allowedRole) => hasRole(allowedRole));
  const canCreateProject = hasRole("Freigeber*in") || hasRole("Admin");

  const saveFiltersToStorage = () => {
    const filterState = {
      filterText,
      typFilter,
      studienartFilter,
      statusFilter,
    };
    localStorage.setItem(STORAGE_FILTER_KEY, JSON.stringify(filterState));
  };

  React.useEffect(() => {
    saveFiltersToStorage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterText, typFilter, studienartFilter, statusFilter]);

  React.useEffect(() => {
    const fetchItems = async () => {
      if (!dataService) return;
      // Only search if filterText is empty or has more than 3 characters
      if (filterText.length > 0 && filterText.length <= 3) return;

      const response = await dataService.getProjektListe(
        filterText,
        typFilter,
        studienartFilter,
        statusFilter,
      );
      if (response) setItems(response);
    };
    fetchItems();
  }, [
    filterText,
    typFilter,
    studienartFilter,
    statusFilter,
    isDrawerOpen,
    dataService,
  ]);

  React.useEffect(() => {
    if (!isDashboardRoot || !hasPermission || !dataService) return;

    const params = new URLSearchParams(location.search);
    const taskGuid = params.get("taskguid");
    const source = params.get("source");

    if (!taskGuid || !source) return;

    const validTabs = [
      "grunddaten",
      "qualifikationsprofil",
      "projektplan",
      "kachelmatrix",
      "curriculummatrix",
      "konkordanzmatrix",
      "kompetenzraster",
      "history",
    ];
    const selectedTab = validTabs.includes(source) ? source : "grunddaten";

    const openProjectByTaskGuid = async () => {
      try {
        const project = await dataService.getProjektWithTaskGuid(taskGuid);

        navigate("/detail", {
          state: {
            projektId: project.itemId,
            projektName: project.bezeichnung ?? "",
            qualifikationsprofil: project.qualifikationsprofil ?? "",
            defaultTab: selectedTab,
          },
        });
        window.scrollTo(0, 0);
      } catch (error) {
        console.error("Failed to open project from taskguid URL:", error);
      }
    };

    void openProjectByTaskGuid();
  }, [dataService, hasPermission, isDashboardRoot, location.search, navigate]);

  const onTypSelect = React.useCallback<
    NonNullable<DropdownProps["onOptionSelect"]>
  >((_, data) => {
    setTypFilter(
      data.selectedOptions.length > 0 ? data.selectedOptions[0] : "Alle Typen",
    );
  }, []);

  const onStudienartSelect = React.useCallback<
    NonNullable<DropdownProps["onOptionSelect"]>
  >((_, data) => {
    setStudienartFilter(
      data.selectedOptions.length > 0
        ? data.selectedOptions[0]
        : "Alle Studienarten",
    );
  }, []);

  const onStatusSelect = React.useCallback<
    NonNullable<DropdownProps["onOptionSelect"]>
  >((_, data) => {
    setStatusFilter(
      data.selectedOptions.length > 0 ? data.selectedOptions[0] : "Alle Status",
    );
  }, []);

  const columns: TableColumnDefinition<IProjektItem>[] = [
    createTableColumn<IProjektItem>({
      columnId: "projektId",
      renderHeaderCell: () => "Projekt-ID",
      compare: (a, b) => (a.projektId ?? "").localeCompare(b.projektId ?? ""),
      renderCell: (item) => (
        <TableCellLayout>{item.projektId ?? ""}</TableCellLayout>
      ),
    }),
    createTableColumn<IProjektItem>({
      columnId: "projektname",
      renderHeaderCell: () => "Projektname",
      compare: (a, b) =>
        (a.bezeichnung ?? "").localeCompare(b.bezeichnung ?? ""),
      renderCell: (item) => (
        <TableCellLayout>{item.bezeichnung ?? ""}</TableCellLayout>
      ),
    }),
    createTableColumn<IProjektItem>({
      columnId: "typ",
      renderHeaderCell: () => "Typ",
      compare: (a, b) => (a.typ ?? "").localeCompare(b.typ ?? ""),
      renderCell: (item) => <TableCellLayout>{item.typ ?? ""}</TableCellLayout>,
    }),
    createTableColumn<IProjektItem>({
      columnId: "art",
      renderHeaderCell: () => "Art",
      compare: (a, b) => (a.art ?? "").localeCompare(b.art ?? ""),
      renderCell: (item) => <TableCellLayout>{item.art ?? ""}</TableCellLayout>,
    }),
    createTableColumn<IProjektItem>({
      columnId: "entwickler",
      renderHeaderCell: () => "Entwickler*in",
      compare: (a, b) =>
        (a.entwicklerName ?? "").localeCompare(b.entwicklerName ?? ""),
      renderCell: (item) => (
        <TableCellLayout>{item.entwicklerName ?? ""}</TableCellLayout>
      ),
    }),
    createTableColumn<IProjektItem>({
      columnId: "freigeber",
      renderHeaderCell: () => "Freigeber*in",
      compare: (a, b) =>
        (a.freigabeName ?? "").localeCompare(b.freigabeName ?? ""),
      renderCell: (item) => (
        <TableCellLayout>{item.freigabeName ?? ""}</TableCellLayout>
      ),
    }),
    createTableColumn<IProjektItem>({
      columnId: "status",
      renderHeaderCell: () => "Status",
      compare: (a, b) => (a.status ?? "").localeCompare(b.status ?? ""),
      renderCell: (item) => (
        <TableCellLayout>{item.status ?? ""}</TableCellLayout>
      ),
    }),
    createTableColumn<IProjektItem>({
      columnId: "modified",
      renderHeaderCell: () => "letzte Änderung",
      compare: (a, b) => {
        const aTime = a.modified ? new Date(a.modified).getTime() : 0;
        const bTime = b.modified ? new Date(b.modified).getTime() : 0;
        return aTime - bTime;
      },
      renderCell: (item) => (
        <TableCellLayout>
          {item.modified ? formatDate(item.modified) : ""}
        </TableCellLayout>
      ),
    }),
  ];

  const handleDrawerClose = async () => {
    setIsDrawerOpen(false);
    setDrawerSize("medium");
  };

  const handleRowClick = (item: IProjektItem) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const state: any = {
      projektId: item.itemId,
      projektName: item.bezeichnung ?? "",
      qualifikationsprofil: item.qualifikationsprofil ?? "",
      defaultTab: "grunddaten",
    };

    navigate(`/detail`, { state });
    window.scrollTo(0, 0);
  };

  return (
    <>
      {isDashboardRoot && roles.length > 0 ? (
        hasPermission ? (
          <div className={styles.container}>
            <div
              className="searchContainer"
              style={{
                display: "flex",
                gap: "10px",
                alignItems: "flex-end",
                marginBottom: "20px",
              }}
            >
              <SearchBox
                placeholder="Suchen..."
                onChange={(_, newValue) => setFilterText(newValue?.value || "")}
                className="searchBox"
                value={filterText}
              />
              {canCreateProject && (
                <Button
                  icon={<Add24Regular />}
                  className="searchActionButton"
                  appearance="primary"
                  onClick={() => setIsDrawerOpen(true)}
                >
                  Neues Projekt
                </Button>
              )}
            </div>
            <div style={{ marginTop: "8px" }}>
              <Dropdown
                title="Filtern nach Typ"
                placeholder="Filtern nach Typ"
                value={typFilter}
                onOptionSelect={onTypSelect}
                style={{ width: "250px" }}
              >
                <Option>Alle Typen</Option>
                <Option>Neuentwicklung</Option>
                <Option>Überarbeitung</Option>
              </Dropdown>
              <Dropdown
                title="Filtern nach Studienart"
                placeholder="Filtern nach Studienart"
                value={studienartFilter}
                onOptionSelect={onStudienartSelect}
                style={{ width: "250px" }}
              >
                <Option>Alle Studienarten</Option>
                <Option>Studiengang</Option>
                <Option>Lehrgang</Option>
              </Dropdown>
              <Dropdown
                title="Filtern nach Status"
                placeholder="Filtern nach Status"
                value={statusFilter}
                onOptionSelect={onStatusSelect}
                style={{ width: "250px" }}
              >
                <Option>Alle Status</Option>
                <Option>In Planung</Option>
                <Option>Freigegeben</Option>
                <Option>Akkreditiert </Option>
                <Option>Akkreditiert mit Auflagen</Option>
                <Option>Wird nicht weiterverfolgt</Option>
              </Dropdown>
            </div>
            {items.length > 0 ? (
              <div className="dataGridContainer">
                <DataGrid
                  items={items}
                  columns={columns}
                  sortable
                  size="medium"
                  aria-label="Dashboard"
                  focusMode="composite"
                  style={{ minWidth: "550px" }}
                >
                  <DataGridHeader>
                    <DataGridRow>
                      {({ renderHeaderCell }) => (
                        <DataGridHeaderCell
                          className="dataGridHeaderCell"
                          style={{
                            backgroundColor: tokens.colorNeutralBackground3,
                            color: tokens.colorNeutralForeground1,
                          }}
                        >
                          {renderHeaderCell()}
                        </DataGridHeaderCell>
                      )}
                    </DataGridRow>
                  </DataGridHeader>
                  <DataGridBody<IProjektItem>>
                    {({ item, rowId }) => {
                      const isAlternateRow = Number(rowId) % 2 === 1;

                      return (
                        <DataGridRow
                          key={rowId}
                          style={{
                            backgroundColor: isAlternateRow
                              ? tokens.colorNeutralBackground2
                              : "transparent",
                            color: tokens.colorNeutralForeground1,
                          }}
                        >
                          {(column) => (
                            <DataGridCell
                              className="dataGridCell"
                              style={{
                                color: tokens.colorNeutralForeground1,
                              }}
                              onClick={() => {
                                handleRowClick(item as IProjektItem);
                              }}
                            >
                              {column.renderCell(item)}
                            </DataGridCell>
                          )}
                        </DataGridRow>
                      );
                    }}
                  </DataGridBody>
                </DataGrid>
              </div>
            ) : (
              <p
                style={{
                  fontSize: "16px",
                  marginBottom: "30px",
                  maxWidth: "500px",
                }}
              >
                Keine Projekte gefunden.
              </p>
            )}
          </div>
        ) : (
          <div className={styles.container}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "40px 20px",
                textAlign: "center",
              }}
            >
              <h1 style={{ color: "#d13438", marginBottom: "20px" }}>
                Zugriff verweigert
              </h1>
              <p
                style={{
                  fontSize: "16px",
                  marginBottom: "30px",
                  maxWidth: "500px",
                }}
              >
                Sie haben keine Berechtigung, auf dieses Dashboard zuzugreifen.
                Bitte kontaktieren Sie einen Administrator, wenn Sie glauben,
                dass dies ein Fehler ist.
              </p>
            </div>
          </div>
        )
      ) : (
        <></>
      )}
      <Drawer
        size={drawerSize}
        open={isDrawerOpen}
        onOpenChange={(_, { open }) => {
          if (!open) {
            handleDrawerClose();
          } else {
            setIsDrawerOpen(true);
            setDrawerSize("medium");
          }
        }}
        position="end"
      >
        <DrawerHeaderTitle
          style={{ paddingLeft: "15px", paddingTop: "15px" }}
          action={
            <Button
              appearance="subtle"
              aria-label="Close"
              icon={<Dismiss24Regular />}
              onClick={() => handleDrawerClose()}
            />
          }
        >
          <Text style={{ fontSize: "1.5rem", fontWeight: 600 }}>
            Neues Projekt anlegen
          </Text>
        </DrawerHeaderTitle>
        <DrawerBody>
          <Projektanlage
            onClose={handleDrawerClose}
            onPageChange={(page) => {
              setDrawerSize(page === "neuanlage" ? "large" : "medium");
            }}
          />
        </DrawerBody>
      </Drawer>
    </>
  );
};
export default Dashboard;
